/**
 * ADMIN ONLY — los cinco numeros del negocio (Mike, 5-oct, §9.29): volumen,
 * ritmo anual, ingreso neto por cobro, comerciantes activos y retencion.
 * Ver backend/services/metricas.py: sale del mismo calculo que Revenue.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { BarChart3, Loader2 } from 'lucide-react';
import { fetchJson, formatMoney as money } from '@/lib/api';

const pct = (r) => (r && r.pct !== null && r.pct !== undefined ? `${r.pct}%` : '—');

const Tarjeta = ({ titulo, valor, nota, testid }) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-4" data-testid={testid}>
    <p className="text-xs uppercase tracking-wide text-slate-500">{titulo}</p>
    <p className="text-2xl font-bold tabular-nums mt-1">{valor}</p>
    {nota && <p className="text-xs text-slate-500 mt-1">{nota}</p>}
  </div>
);

export default function AdminMetricsPage() {
  const { token } = useAuth();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchJson('/admin/metrics?months=12', { token }).then(setD).catch((e) => setError(e.message));
  }, [token]);

  const u = d?.last_30_days;
  return (
    <div className="space-y-6" data-testid="admin-metrics">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2"><BarChart3 className="w-6 h-6 text-indigo-600" /> Business metrics</h1>
        <p className="text-sm text-slate-500">The five numbers investors ask for, measured from what SelleSend records. Same source as Revenue.</p>
      </div>

      {error && <p className="text-sm text-rose-700">{error}</p>}
      {!d && !error && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>}

      {d && (
        <>
          <p className="text-xs uppercase tracking-wide text-slate-500">Last 30 days</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Tarjeta titulo="Volume (TPV)" valor={money(u.volume)} nota={`${u.charges} charges`} testid="metric-volume" />
            <Tarjeta titulo="Annual run rate" valor={money(u.run_rate)} nota="Net revenue × 12. Not ARR: no subscriptions yet." testid="metric-run-rate" />
            <Tarjeta titulo="Net revenue per charge" valor={u.net_per_charge === null ? '—' : money(u.net_per_charge)} nota={`Net revenue ${money(u.net_revenue)}`} testid="metric-net-per-charge" />
            <Tarjeta titulo="Active merchants" valor={u.active_merchants} nota="At least one charge" testid="metric-active" />
            <Tarjeta titulo="Retention" valor={pct(u.retention)} nota={`${u.retention.retained} of ${u.retention.previous} from the 30 days before`} testid="metric-retention" />
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-x-auto">
            <table className="w-full text-sm" data-testid="metrics-months">
              <thead className="bg-slate-50 dark:bg-slate-900 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Month</th>
                  <th className="text-right px-3 py-2">Volume</th>
                  <th className="text-right px-3 py-2">Charges</th>
                  <th className="text-right px-3 py-2">Net revenue</th>
                  <th className="text-right px-3 py-2">Net per charge</th>
                  <th className="text-right px-3 py-2">Active merchants</th>
                  <th className="text-right px-3 py-2">Retention</th>
                </tr>
              </thead>
              <tbody>
                {[...d.months].reverse().map((m) => (
                  <tr key={m.month} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="px-3 py-2">{m.month}{m.partial && <span className="text-xs text-slate-500"> (so far)</span>}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(m.volume)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{m.charges}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(m.net_revenue)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{m.net_per_charge === null ? '—' : money(m.net_per_charge)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{m.active_merchants}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{pct(m.retention)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl bg-slate-50 dark:bg-slate-900 p-4 text-xs text-slate-600 space-y-1" data-testid="metrics-definitions">
            <p className="font-semibold text-slate-700">How each number is measured</p>
            <p><b>Volume:</b> {d.definitions.volume}</p>
            <p><b>Net revenue:</b> {d.definitions.net_revenue}</p>
            <p><b>Annual run rate:</b> {d.definitions.run_rate}</p>
            <p><b>Net per charge:</b> {d.definitions.net_per_charge}</p>
            <p><b>Active merchants:</b> {d.definitions.active_merchants}</p>
            <p><b>Retention:</b> {d.definitions.retention}</p>
          </div>
        </>
      )}
    </div>
  );
}
